import styled, { css } from 'styled-components';

// CONSTANTS
import { responsiveEndPoints } from '../../constants/responsive-endpoints';
import { theme } from '../../constants/theme';

interface TableStyleProps {
  activeTab: boolean;
}

export const Tabs = styled.div`
  width: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const TabsTitle = styled.span`
  padding: 0 0 32px 32px;
  font-size: 11px;
  line-height: 16px;
  color: ${theme.colors.secondary.gray300};
  font-weight: ${theme.weights.semibold};
  letter-spacing: 0.03em;
  text-transform: uppercase;

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    padding: 0 0 24px 16px;
  }
`;

export const TabHeader = styled.ul`
  display: flex;
  flex-direction: row;
  position: relative;
  padding-left: 32px;
  padding-right: 32px;
  margin-bottom: 32px;
  border-bottom: 1px solid ${theme.colors.secondary.gray450};

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    padding-left: 4px;
    padding-right: 0;
    margin-bottom: 24px;
    justify-content: space-between;
    border-bottom: none;
    overflow-x: auto;
    overflow-y: hidden;
    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const TabTitle = styled.span`
  padding-right: 8px;
  cursor: pointer;
  z-index: 2;
  @media (max-width: ${responsiveEndPoints.mobile}px) {
    white-space: nowrap;
  }
`;

export const TabIcon = styled.div<TableStyleProps>`
  display: flex;
  svg {
    ${({ activeTab }: TableStyleProps) =>
      !activeTab &&
      css`
        rect {
          fill: ${theme.colors.secondary.gray400};
        }
      `}
  }
`;

export const ContentHeader = styled.li<TableStyleProps>`
  display: flex;
  align-items: center;
  padding: 8px 32px;
  position: relative;
  color: ${theme.colors.secondary.gray300};
  font-size: 15px;
  line-height: 24px;
  font-weight: ${theme.weights.semibold};
  font-style: normal;

  border-top: 1px solid ${theme.colors.primary.white};
  border-left: 1px solid ${theme.colors.primary.white};
  border-right: 1px solid ${theme.colors.primary.white};

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    font-size: 14px;
    height: 36px;
    padding: 8px 16px;
    justify-content: space-between;

    ${({ activeTab }) =>
      !activeTab &&
      css`
        border-bottom: 1px solid ${theme.colors.secondary.gray450};
      `};
  }

  svg {
    z-index: 1;
    left: 0;
    right: 0;
    top: 0;
  }

  ${({ activeTab }) =>
    activeTab &&
    css`
      color: ${theme.colors.primary.black};
      background-color: ${theme.colors.primary.white};
      z-index: 1;
      position: relative;
      box-shadow: 1px 1px 0 ${theme.colors.primary.white};
      border-top: 1px solid ${theme.colors.secondary.gray450};
      border-left: 1px solid ${theme.colors.secondary.gray450};
      border-right: 1px solid ${theme.colors.secondary.gray450};
      border-top-right-radius: 8px;
      border-top-left-radius: 8px;
      border-bottom: none;

      &:after,
      &:before {
        content: '';
        border: 1px solid ${theme.colors.secondary.gray450};
        position: absolute;
        bottom: -1px;
        width: 8.5px;
        height: 8px;
      }

      &:after {
        right: -9.5px;
        border-bottom-left-radius: 8px;
        border-width: 0px 0px 1px 1px;
        box-shadow: -2px 2px 0 ${theme.colors.primary.white};
      }

      &:before {
        left: -9.5px;
        border-bottom-right-radius: 8px;
        border-width: 0 1px 1px 0;
        box-shadow: 2px 2px 0 ${theme.colors.primary.white};
      }

      @media (max-width: ${responsiveEndPoints.mobile}px) {
        &:before {
          left: -9px;
        }

        &:after {
          right: -9px;
        }
      }
    `}

  ${({ activeTab }) =>
    !activeTab &&
    css`
      &:hover {
        background-color: ${theme.colors.secondary.gray600};
        position: relative;
        border-top: 1px solid ${theme.colors.secondary.gray500};
        border-left: 1px solid ${theme.colors.secondary.gray500};
        border-right: 1px solid ${theme.colors.secondary.gray500};
        border-top-right-radius: 8px;
        border-top-left-radius: 8px;

        &:after,
        &:before {
          content: '';
          border: 1px solid ${theme.colors.secondary.gray500};
          position: absolute;
          bottom: -1px;
          width: 8px;
          height: 8px;
        }

        &:after {
          right: -9.5px;
          border-bottom-left-radius: 8px;
          border-width: 0px 0px 1px 1px;
          box-shadow: -2px 2px 0 ${theme.colors.secondary.gray600};
        }

        &:before {
          left: -9.5px;
          border-bottom-right-radius: 8px;
          border-width: 0 1px 1px 0;
          box-shadow: 2px 2px 0 ${theme.colors.secondary.gray600};
        }
      }

      @media (max-width: ${responsiveEndPoints.mobile}px) {
        &:after,
        &:before {
          content: '';
          border: 1px solid ${theme.colors.secondary.gray450};
          position: absolute;
          bottom: -1px;
          width: 8px;
          height: 8px;
        }

        &:before {
          width: 35px;
          left: -35px;
          border-width: 0 0 1px 0;
        }

        &:after {
          width: 30px;
          right: -20px;
          border-width: 0 0 1px 0;
        }
      }
    `}
`;

export const TabContent = styled.div<TableStyleProps>`
  display: block;
  width: 100%;
  padding-left: 32px;
  padding-right: 32px;

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  ${({ activeTab }) =>
    !activeTab &&
    css`
      display: none;
    `}
`;
